import React, { useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Alert, Box, Button, Paper, Stack, Typography } from "@mui/material";

import { HttpAuth, syncTokensAfterLogin } from "../../http";
import { userStorage } from "../../utils/storage";
import { AuthorizationResponse } from "../../types/user/userAuthModels";
import { ControlledTextField } from "../../components/forms/ControlledTextField";
import { ControlledTextPasswordFieldFilled } from "../../components/forms/ControlledTextPasswordField";

import { BUTTON_STYLES } from "../../styles/buttonStyles";
import { TYPOGRAPHY_STYLES } from "../../styles/typographyStyles";

import { couponFieldStyle } from "../../components/Checkout/CouponStyles";

const schema = yup.object({
    mail: yup.string().email("Email inválido").required("El email es obligatorio"),
    password: yup.string().required("La contraseña es obligatoria"),
});

const TEXT = {
    title: {
        ...TYPOGRAPHY_STYLES.rubik,
        fontSize: 24,
        fontWeight: 700,
    },
} as const;

export default function LogIn() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const mailInicial = useMemo(() => searchParams.get("mail")?.trim() ?? "", [searchParams]);
    const mostrarExitoRestablecer = searchParams.get("restablecida") === "1";

    const [error, setError] = useState<string>();
    const [loading, setLoading] = useState(false);

    const { handleSubmit, control } = useForm({
        resolver: yupResolver(schema),
        defaultValues: { mail: mailInicial, password: "" },
    });

    const mailActual = useWatch({ control, name: "mail" }) ?? "";

    const onSubmit = async (data: any) => {
        setError(undefined);
        setLoading(true);

        try {
            const response: AuthorizationResponse = await HttpAuth.authenticateUser(
                data.mail,
                data.password
            );

            if (response.tieneError) {
                setError(response.descripcionError);
                return;
            }

            userStorage.save(
                response.idUsuario,
                response.nombre,
                response.apellido,
                response.apellidoNombre,
                response.codUsuarioEstado,
                response.accessToken,
                response.refreshToken,
                response.idPerfil,
                response.codigoArea,
                response.telefono
            );

            syncTokensAfterLogin(response.accessToken, response.refreshToken);

            navigate("/");
        } catch (err: any) {
            setError(err.message ?? "Error desconocido");
        } finally {
            setLoading(false);
        }
    };

    const irARecupero = () => {
        const mail = String(mailActual ?? "").trim();
        navigate(
            `/recupero-contrasenia${mail ? `?mail=${encodeURIComponent(mail)}` : ""}`
        );
    };

    return (
        <Box sx={{ maxWidth: 420, mx: "auto", mt: 4, mb: 10 }}>
            <Paper
                sx={{
                    p: 3,
                    border: "1px solid #ddd",
                    borderRadius: 2,
                }}
            >
                <Typography sx={{ ...TEXT.title, mb: 3 }}>Iniciar sesión</Typography>

                <form onSubmit={handleSubmit(onSubmit)}>
                    <Stack spacing={2.5}>
                        {mostrarExitoRestablecer && (
                            <Alert severity="success">
                                Contraseña actualizada correctamente. Ya podés iniciar sesión.
                            </Alert>
                        )}

                        <ControlledTextField control={control} name="mail" label="Email" />

                        <ControlledTextPasswordFieldFilled
                            control={control}
                            name="password"
                            label="Contraseña"
                            sx={couponFieldStyle}
                        />

                        <Button
                            type="button"
                            variant="text"
                            onClick={irARecupero}
                            sx={{
                                width: "fit-content",
                                minWidth: "auto",
                                p: 0,
                                mt: -1,
                                textTransform: "none",
                            }}
                        >
                            Olvidé mi contraseña
                        </Button>

                        <Button
                            variant="contained"
                            type="submit"
                            disabled={loading}
                            sx={{ ...BUTTON_STYLES.invertedPrimary, width: "100%" }}
                        >
                            {loading ? "Ingresando..." : "Ingresar"}
                        </Button>

                        <Button
                            variant="outlined"
                            onClick={() => navigate("/")}
                            sx={{
                                ...(BUTTON_STYLES as any).outlinedPrimary,
                                width: "100%",
                            }}
                        >
                            Volver
                        </Button>

                        {error && <Alert severity="error">{error}</Alert>}
                    </Stack>
                </form>
            </Paper>
        </Box>
    );
}